import type {
    BaseBreakPointConfigProtocol,
    BlockAbstract,
    DataSourceAbstract,
    DataSourceBase,
    PageAbstract,
    TextBlockConfig
} from '@lighthouse/core'
import { BlockType } from '@lighthouse/core'
import type { NodeIdWithScope } from '@lighthouse/shared'
import {
    ANONYMOUS,
    deepMerge,
    EMPTY_COLUMN_GROUP,
    FindUseLocationType,
    FindUseObjectContextProvider,
    getBreakPointConfigure,
    hasChildrenBlock,
    mergeSingleBreakPointConfigure,
    PERSON_ID,
    systemPersonFieldId,
    useApplicationContext
} from '@lighthouse/shared'
import { deepOmitNil } from '@lighthouse/tools'
import fastEqual from 'fast-deep-equal/es6'
import { useSetAtom } from 'jotai'
import { clone, findIndex } from 'rambda'
import React, { useCallback, useEffect, useMemo } from 'react'
import type { DeepPartial } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'
import { useLatest } from 'react-use'

import { BlockSettingProvider } from '@/contexts/BlockSettingContext'
import { BreakPointProvider } from '@/contexts/BreakPointContext'
import { useCurrentBreakPointId, useMainBreakPoint } from '@/hooks/useApplication'
import { useFindUse } from '@/hooks/useFindUse'

import { applicationAtom, dataSourceAtom, dataSourceListIdsAtom } from './atoms'
import { AudioBlockSetting } from './Audio'
import { BreadcrumbSetting } from './Breadcrumb'
import { ButtonSetting } from './Button'
import { CardBlockSetting } from './Card'
import { ChartBlockSettings } from './Chart'
import { CollapseSetting } from './Collapse'
import { ContainerSetting } from './Container'
import { CustomViewSetting } from './CustomView'
import { DividerBlockSetting } from './Divider'
import BlockFieldSettings from './Field'
import { FieldGroupSetting } from './FieldGroup'
import FileBlockSetting from './File'
import { FilterSetting } from './Filter'
import { FloatSetting } from './Float'
import { IconSetting } from './Icon'
import { IframeSetting } from './Iframe'
import { BlockImageSettings } from './Image'
import { OnOffSetting } from './OnOff'
import { QrBarcodeSetting } from './QrBarcode'
import * as SC from './styles'
import { SubFormSetting } from './SubForm'
import { TabsSettings } from './Tabs'
import { TextBlockSetting } from './TextV2'
import { TocBlockSetting } from './TocBlock'
import { UserCenterSetting } from './UserCenter'
import { VideoBlockSetting } from './Video'
import BlockViewSettings from './View'

export interface BlockSettingsFormValue {
    title?: string
    type?: BlockType
    breakPoint?: any
    kanbanGroupConfigure?: {
        groupByFieldId?: string
        groupConfig?: Array<{ value: string }>
    }
    content?: unknown
    htmlValue?: unknown
    [key: string]: any
}

interface BlockSettingsProps {
    containerConfigureLoading?: boolean
    appId: string
    value: BlockAbstract
    node: NodeIdWithScope
    onChange?: (v: BlockAbstract) => void
    dataSourceList: DataSourceBase[]
    dataSource?: DataSourceAbstract
    allDataSources: DataSourceAbstract[]
    pointer: string
    allPages: PageAbstract[]
    allViews?: { title: string; dsId: string; id: string }[]
}

const getBlockConfigFromFormValue = (values: DeepPartial<BlockSettingsFormValue>) => {
    const { title, type, ...config } = values

    if (type === BlockType.text || type === BlockType.qrBarcode) {
        const { content, htmlValue, ...rest } = config
        return { content: { value: content, htmlValue }, ...rest } as TextBlockConfig
    }

    return config
}
// inherit
export const BlockSettings = React.forwardRef<HTMLDivElement, BlockSettingsProps>(
    (
        {
            containerConfigureLoading,
            appId,
            value,
            node,
            pointer,
            onChange,
            dataSourceList,
            dataSource,
            allDataSources,
            allPages,
            allViews
        },
        ref
    ) => {
        const breakPointId = useCurrentBreakPointId()
        const mainBreakPoint = useMainBreakPoint()
        // 获取外部package传入的数据源数据，在组件内部存储atom
        const updateDataSource = useSetAtom(dataSourceAtom)
        const updateDataSourceList = useSetAtom(dataSourceListIdsAtom)
        const updateApplicationInfo = useSetAtom(applicationAtom)
        const findUseObject = useFindUse(FindUseLocationType.BLOCK, { id: value.id })
        const { personOptions } = useApplicationContext()
        const latest = useLatest({ value, onChange, personOptions })

        const mergeValue = useMemo<BlockSettingsFormValue>(() => {
            const breakPoint = getBreakPointConfigure(breakPointId, mainBreakPoint, value.config.breakPoint, value.config.breakPoints)
            return {
                ...value.config,
                breakPoint,
                content: value.type === BlockType.text || value.type === BlockType.qrBarcode ? value.config.content.value : undefined,
                htmlValue: value.type === BlockType.text ? value.config.content.htmlValue : undefined,
                title: value.title,
                type: value.type
            }
        }, [breakPointId, mainBreakPoint, value.config, value.title, value.type])

        const mergeLatest = useLatest(mergeValue)
        const methods = useForm<BlockSettingsFormValue>({
            values: mergeValue
        })

        const handleSyncOne = useCallback(
            (name: string | string[], previewId: string) => {
                const block = clone(latest.current.value)
                if (previewId === mainBreakPoint && block.config.breakPoint.id === previewId) {
                    return
                }
                const path = Array.isArray(name) ? name : [name]
                const mergeBreakPoint = mergeLatest.current.breakPoint
                const target = deepMerge(block.config.breakPoint, mergeBreakPoint, path)
                const index = findIndex(item => item?.id === previewId, block.config.breakPoints)
                if (index >= 0) {
                    block.config.breakPoints[index].breakKeys = block.config.breakPoints[index].breakKeys?.filter(key => {
                        if (typeof name === 'string') {
                            return key !== name
                        }
                        return !name.includes(key)
                    })
                }
                block.config.breakPoint = target
                latest.current.onChange?.(block)
            },
            [latest, mergeLatest, mainBreakPoint]
        )

        const handleSyncAll = useCallback(
            (previewId: string) => {
                const block = clone(latest.current.value)
                if (previewId === mainBreakPoint && block.config.breakPoint.id === previewId) {
                    return
                }
                block.config.breakPoint = mergeLatest.current.breakPoint
                const index = findIndex(item => item?.id === previewId, block.config.breakPoints)
                if (index >= 0) {
                    block.config.breakPoints[index].breakKeys = []
                }
                latest.current.onChange?.(block)
            },
            [latest, mergeLatest, mainBreakPoint]
        )

        useEffect(() => {
            updateDataSource(dataSource)
            return () => {
                updateDataSource(undefined)
            }
        }, [updateDataSource, dataSource])

        useEffect(() => {
            updateDataSourceList(dataSourceList)
        }, [updateDataSourceList, dataSourceList, allDataSources, appId])

        useEffect(() => {
            updateApplicationInfo(draft => {
                draft.pageList = allPages
                draft.dataSourceList = allDataSources.filter(item => item.appId === appId)
                draft.viewList = allViews ?? []
            })
        }, [updateApplicationInfo, allPages, allDataSources, allViews, appId])

        const extraSetting = useMemo(() => {
            switch (value.type) {
                case BlockType.view: {
                    return value.config.viewType === 'custom' ? <CustomViewSetting id={value.id} /> : <BlockViewSettings id={value.id} />
                }
                case BlockType.chart: {
                    return <ChartBlockSettings />
                }
                case BlockType.iframe: {
                    return <IframeSetting />
                }
                case BlockType.field: {
                    return <BlockFieldSettings pointer={pointer} id={value.id} />
                }
                case BlockType.image: {
                    return <BlockImageSettings blockId={value.id} allPages={allPages} />
                }
                case BlockType.button: {
                    return <ButtonSetting allPages={allPages} />
                }
                case BlockType.card: {
                    return <CardBlockSetting allPages={allPages} />
                }
                case BlockType.divider: {
                    return <DividerBlockSetting />
                }
                case BlockType.video: {
                    return <VideoBlockSetting />
                }
                case BlockType.audio: {
                    return <AudioBlockSetting />
                }
                case BlockType.fieldGroup: {
                    return <FieldGroupSetting id={value.id} />
                }
                case BlockType.breadcrumb: {
                    return <BreadcrumbSetting />
                }
                case BlockType.collapse: {
                    return <CollapseSetting />
                }
                case BlockType.file: {
                    return <FileBlockSetting blockId={value.id} />
                }
                case BlockType.container:
                case BlockType.formContainer: {
                    return <ContainerSetting type={value.type} containerConfigureLoading={containerConfigureLoading} />
                }
                case BlockType.tabs: {
                    return <TabsSettings />
                }
                case BlockType.text: {
                    return <TextBlockSetting />
                }
                case BlockType.filter: {
                    return <FilterSetting blockId={value.id} />
                }
                case BlockType.qrBarcode: {
                    return <QrBarcodeSetting />
                }
                case BlockType.subForm: {
                    return <SubFormSetting blockId={value.id} />
                }
                case BlockType.icon: {
                    return <IconSetting />
                }
                case BlockType.userCenter: {
                    return <UserCenterSetting />
                }
                case BlockType.floatBox: {
                    return <FloatSetting />
                }
                case BlockType.onOff: {
                    return <OnOffSetting allPages={allPages} />
                }
                case BlockType.toc: {
                    return <TocBlockSetting />
                }
                default: {
                    return null
                }
            }
        }, [containerConfigureLoading, allPages, pointer, value.config, value.id, value.type])

        useEffect(() => {
            const { unsubscribe } = methods.watch((formValue, info) => {
                if (fastEqual(deepOmitNil(mergeLatest.current), deepOmitNil(formValue))) {
                    return
                }

                const { title = '', type, breakPoint } = formValue
                const block = latest.current.value
                // const block = {config: mergeLatest.current}
                const blockConfig = { ...block.config, ...getBlockConfigFromFormValue(formValue) }

                const validBreakPoint = clone(breakPoint)
                if (breakPointId !== mainBreakPoint) {
                    blockConfig.breakPoint = block.config.breakPoint
                    const newBreakPoint = mergeSingleBreakPointConfigure(
                        block.config.breakPoint,
                        validBreakPoint as Partial<BaseBreakPointConfigProtocol>
                    )
                    const index = findIndex(item => item?.id === breakPointId, blockConfig.breakPoints)
                    if (index >= 0) {
                        blockConfig.breakPoints[index] = {
                            ...newBreakPoint,
                            id: blockConfig.breakPoints[index]?.id
                        }
                    } else {
                        blockConfig.breakPoints.push({
                            ...(newBreakPoint as any),
                            id: breakPointId
                        })
                    }
                }
                const children = hasChildrenBlock(block) ? block.children : undefined
                const newBlockAbstract = {
                    title,
                    type,
                    config: blockConfig,
                    id: block.id,
                    children,
                    synchronousId: latest.current.value.synchronousId,
                    isMasterSynchronous: latest.current.value.isMasterSynchronous,
                    isLeafSynchronous: latest.current.value.isLeafSynchronous
                } as BlockAbstract
                latest.current.onChange?.(clone(newBlockAbstract))
            })
            return unsubscribe
        }, [methods, latest, breakPointId, value.config.breakPoint, mergeLatest, mainBreakPoint])

        // 检查配置有效性
        useEffect(() => {
            if (!dataSource) {
                return
            }
            // 看板视图分组配置检验
            // const isKanbanView = value.type === 'view' && value.config.viewType === 'kanban'
            if (value.type === 'view' && value.config.viewType === 'kanban') {
                const { kanbanGroupConfigure } = value.config
                const { groupByFieldId, groupConfig } = kanbanGroupConfigure || {}
                const hasKanbanConfig = !!groupByFieldId && !!groupConfig
                if (hasKanbanConfig) {
                    const field = dataSource.schema[groupByFieldId]
                    if (!field) {
                        const init: any = {
                            groupByFieldId: '',
                            groupConfig: []
                        }
                        methods.setValue('kanbanGroupConfigure', init)
                        return
                    }
                    if (field.type === 'select') {
                        const options = new Set([...field.select.options.map(item => item.label), EMPTY_COLUMN_GROUP])
                        // 删除过期的选项
                        const newConfig = groupConfig.filter(item => options.has(item.value))

                        if (!fastEqual(groupConfig, newConfig)) {
                            const kanbanGroupConfigure: any = {
                                groupByFieldId,
                                groupConfig: newConfig
                            }
                            methods.setValue('kanbanGroupConfigure', kanbanGroupConfigure)
                        }
                    }
                    if (field.type === 'person') {
                        const isSystemPerson = systemPersonFieldId.has(field.id)
                        const staticAnonymous = isSystemPerson ? [...PERSON_ID] : [ANONYMOUS]

                        const options = new Set([
                            ...latest.current.personOptions.map(item => item.id),
                            ...staticAnonymous,
                            EMPTY_COLUMN_GROUP
                        ])
                        const newConfig = groupConfig.filter(item => options.has(item.value))
                        if (!fastEqual(groupConfig, newConfig)) {
                            const kanbanGroupConfigure: any = {
                                groupByFieldId,
                                groupConfig: newConfig
                            }
                            methods.setValue('kanbanGroupConfigure', kanbanGroupConfigure)
                        }
                    }
                }
            }
        }, [dataSource, latest, methods, value.config, value.type])

        return (
            <SC.BlockSettingContainer ref={ref}>
                <FindUseObjectContextProvider value={findUseObject}>
                    <BlockSettingProvider blockId={value.id} scope={node.scope}>
                        <BreakPointProvider onSyncOne={handleSyncOne} onSyncAll={handleSyncAll}>
                            <SC.BlockSettingContentContainer>
                                <FormProvider {...methods}>{extraSetting}</FormProvider>
                            </SC.BlockSettingContentContainer>
                        </BreakPointProvider>
                    </BlockSettingProvider>
                </FindUseObjectContextProvider>
            </SC.BlockSettingContainer>
        )
    }
)
